/**
 * Moldura da aplicação (Fase B do redesign): menu lateral café com ícones, grupos que abrem e
 * fecham, menu recolhível só com ícones (preferência guardada neste navegador), gaveta no
 * celular e cabeçalho com empresa ativa, ambiente fiscal e usuário. O menu continua vindo de
 * `menu.ts` e respeitando as permissões (`visibleMenu`).
 */
import { APP_NAME, formatCnpj, type FiscalSettings } from "@cafegestor/shared";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type KeyboardEvent } from "react";
import { BrandMark, Icon, type IconName } from "@/components/icons";
import { NoticeArea } from "@/components/NoticeArea";
import { StatusBadge, type Tone } from "@/components/ui";
import { apiFetch } from "@/lib/api";
import { setActiveCompanyId } from "@/lib/company-store";
import { FISCAL_KEY } from "@/features/fiscal/lib";
import { useAuth } from "./auth";
import { useCan, useMe } from "./me";
import { groupOfPath, visibleMenu, type MenuItem } from "./menu";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

const GROUP_ICON: Record<string, IconName> = {
  Geral: "inicio",
  Cadastros: "cadastros",
  Vendas: "vendas",
  Fiscal: "fiscal",
  Compras: "compras",
  Financeiro: "financeiro",
  Produção: "producao",
  Estoque: "estoque",
  Administração: "administracao",
  Plataforma: "plataforma",
};

const groupId = (label: string) =>
  `menu-${label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

/** Preferência do menu recolhido (só conveniência deste navegador; falha = menu aberto). */
const COMPACT_KEY = "cafegestor.menu.recolhido";
function readCompact(): boolean {
  try {
    return localStorage.getItem(COMPACT_KEY) === "1";
  } catch {
    return false;
  }
}
function writeCompact(v: boolean) {
  try {
    localStorage.setItem(COMPACT_KEY, v ? "1" : "0");
  } catch {
    // navegador sem armazenamento: a preferência vale só nesta visita
  }
}

const ENV_BADGE: Record<FiscalSettings["environment"], { label: string; tone: Tone }> = {
  simulador: { label: "NF-e: Simulador", tone: "neutral" },
  homologacao: { label: "NF-e: Homologação", tone: "warn" },
  producao: { label: "NF-e: Produção", tone: "ok" },
};

function initials(name: string): string {
  const parts = name
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function AppShell() {
  const { data: me } = useMe();
  const can = useCan();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState(false);
  const [compactPref, setCompact] = useState(readCompact);

  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const visible = visibleMenu({
    isPlatformAdmin: Boolean(me?.user.isPlatformAdmin),
    hasCompany: Boolean(me?.activeCompany),
    can,
  });
  // grupos recolhidos ao entrar; o grupo da página atual abre sozinho (também ao navegar)
  const currentGroup = groupOfPath(visible, pathname);
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(
    () => new Set(currentGroup ? [currentGroup] : []),
  );
  useEffect(() => {
    if (!currentGroup) return;
    setOpenGroups((s) => (s.has(currentGroup) ? s : new Set([...s, currentGroup])));
  }, [currentGroup]);
  // trocar de página fecha a gaveta do celular
  useEffect(() => setDrawer(false), [pathname]);

  // na gaveta do celular o menu aparece sempre completo
  const compact = compactPref && !drawer;
  const setCompactPref = (v: boolean) => {
    setCompact(v);
    writeCompact(v);
  };
  const toggle = (label: string) => {
    if (compact) {
      // recolhido (só ícones): o clique abre o menu já com o grupo aberto
      setCompactPref(false);
      setOpenGroups((s) => new Set([...s, label]));
      return;
    }
    setOpenGroups((s) => {
      const next = new Set(s);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const seesFiscal = Boolean(me?.activeCompany) && can("fiscal.ver");
  const fiscal = useQuery({
    queryKey: [...FISCAL_KEY, "/configuracao"],
    queryFn: () => apiFetch<FiscalSettings>("/fiscal/configuracao"),
    enabled: seesFiscal,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const env =
    seesFiscal && fiscal.data && fiscal.data.provider !== "desligado"
      ? ENV_BADGE[fiscal.data.environment]
      : null;

  const company = me?.companies.find((c) => c.id === me.activeCompany?.id);
  const userName = me?.user.fullName ?? me?.user.login?.username ?? me?.user.email ?? "";
  const labelCls = compact ? "lg:sr-only" : "";

  const itemLink = (i: MenuItem) => (
    <li key={i.to}>
      <Link
        to={i.to}
        className="flex min-h-9 items-center rounded-lg px-3 text-sm text-sidebar-foreground hover:bg-white/8 hover:text-white data-[status=active]:font-semibold data-[status=active]:text-sidebar-accent-foreground data-[status=active]:hover:bg-sidebar-accent"
        activeProps={{ className: "bg-sidebar-accent", "aria-current": "page" }}
        activeOptions={{ exact: i.to === "/" }}
      >
        {i.label}
      </Link>
    </li>
  );

  function onDrawerKey(e: KeyboardEvent<HTMLElement>) {
    if (e.key === "Escape" && drawer) setDrawer(false);
  }

  return (
    <div className="min-h-screen bg-background text-foreground lg:flex">
      {drawer && (
        <div
          aria-hidden
          className="fixed inset-0 z-30 bg-foreground/45 lg:hidden"
          onClick={() => setDrawer(false)}
        />
      )}
      <aside
        id="menu-lateral"
        onKeyDown={onDrawerKey}
        className={cx(
          "z-40 flex-col bg-sidebar text-sidebar-foreground",
          drawer ? "fixed inset-y-0 left-0 flex w-72 shadow-pop" : "hidden",
          "lg:sticky lg:top-0 lg:flex lg:h-screen lg:shrink-0 lg:shadow-none",
          compact ? "lg:w-[76px]" : "lg:w-64",
        )}
      >
        <div
          className={cx(
            "flex items-center gap-3 border-b border-sidebar-border px-4 py-4",
            compact && "lg:justify-center lg:px-0",
          )}
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary">
            <BrandMark />
          </span>
          <span className={cx("min-w-0 flex-col", compact ? "flex lg:hidden" : "flex")}>
            <span className="text-[17px] font-bold tracking-tight text-white">{APP_NAME}</span>
            <span className="text-xs text-sidebar-foreground/80">Gestão para fábricas de café</span>
          </span>
          <button
            type="button"
            className="ml-auto flex size-10 items-center justify-center rounded-lg text-sidebar-foreground hover:bg-white/8 lg:hidden"
            aria-label="Fechar menu"
            onClick={() => setDrawer(false)}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 py-3" aria-label="Menu principal">
          {visible.map((g) =>
            g.label === "Geral" ? (
              <ul key={g.label} className="space-y-0.5 pb-1.5">
                {g.items.map((i) => (
                  <li key={i.to}>
                    <Link
                      to={i.to}
                      title={compact ? i.label : undefined}
                      className={cx(
                        "flex min-h-11 items-center gap-3 rounded-lg px-3 text-[15px] font-medium text-sidebar-foreground hover:bg-white/8 hover:text-white data-[status=active]:text-sidebar-accent-foreground data-[status=active]:hover:bg-sidebar-accent",
                        compact && "lg:justify-center lg:px-0",
                      )}
                      activeProps={{ className: "bg-sidebar-accent", "aria-current": "page" }}
                      activeOptions={{ exact: i.to === "/" }}
                    >
                      <Icon name="inicio" className="shrink-0" />
                      <span className={labelCls}>{i.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div key={g.label}>
                <button
                  type="button"
                  title={compact ? g.label : undefined}
                  className={cx(
                    "flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-[15px] font-medium hover:bg-white/8 hover:text-white",
                    currentGroup === g.label ? "text-white" : "text-sidebar-foreground",
                    compact && currentGroup === g.label && "lg:bg-sidebar-accent",
                    compact && "lg:justify-center lg:px-0",
                  )}
                  aria-expanded={!compact && openGroups.has(g.label)}
                  aria-controls={groupId(g.label)}
                  onClick={() => toggle(g.label)}
                >
                  <Icon name={GROUP_ICON[g.label] ?? "plataforma"} className="shrink-0" />
                  <span className={cx("flex-1", labelCls)}>{g.label}</span>
                  <Icon
                    name="chevronRight"
                    size={14}
                    strokeWidth={2}
                    className={cx(
                      "shrink-0 opacity-70 transition-transform",
                      openGroups.has(g.label) && "rotate-90",
                      compact && "lg:hidden",
                    )}
                  />
                </button>
                <ul
                  id={groupId(g.label)}
                  hidden={compact || !openGroups.has(g.label)}
                  className="space-y-0.5 pb-2 pl-9 pt-0.5"
                >
                  {g.items.map(itemLink)}
                </ul>
              </div>
            ),
          )}
        </nav>
        <div className="hidden border-t border-sidebar-border px-2.5 py-3 lg:block">
          <button
            type="button"
            onClick={() => setCompactPref(!compactPref)}
            aria-label={compact ? "Expandir menu" : "Recolher menu"}
            className={cx(
              "flex min-h-10 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-sidebar-foreground/90 hover:bg-white/8 hover:text-white",
              compact && "justify-center px-0",
            )}
          >
            <Icon name={compact ? "expand" : "collapse"} size={18} />
            {!compact && <span aria-hidden>Recolher menu</span>}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex flex-wrap items-center gap-x-4 gap-y-2 border-b bg-card/95 px-4 py-2.5 backdrop-blur sm:px-6">
          <button
            type="button"
            className="flex size-11 items-center justify-center rounded-lg border border-input text-secondary-foreground hover:bg-muted lg:hidden"
            aria-label="Abrir menu"
            aria-controls="menu-lateral"
            aria-expanded={drawer}
            onClick={() => setDrawer(true)}
          >
            <Icon name="menu" size={20} />
          </button>
          {company ? (
            <div className="flex min-w-0 flex-col text-sm leading-tight">
              <span className="truncate font-semibold">{company.name}</span>
              <span className="text-[13px] text-muted-foreground">{formatCnpj(company.cnpj)}</span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">Nenhuma empresa selecionada</span>
          )}
          {(me?.companies.length ?? 0) > 1 && (
            <Link
              to="/selecionar-empresa"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[13px] font-medium text-primary hover:bg-muted"
            >
              <Icon name="swap" size={15} strokeWidth={2} />
              Trocar empresa
            </Link>
          )}
          {env && <StatusBadge tone={env.tone}>{env.label}</StatusBadge>}
          <div className="ml-auto flex items-center gap-2.5">
            <span
              aria-hidden
              className="hidden size-9 items-center justify-center rounded-full bg-primary text-[13px] font-semibold text-primary-foreground sm:flex"
            >
              {initials(userName)}
            </span>
            <span className="hidden max-w-48 truncate text-sm font-medium sm:block">
              {userName}
            </span>
            <Link
              to="/minha-senha"
              className="hidden h-9 items-center rounded-lg px-2 text-sm text-muted-foreground hover:bg-muted sm:inline-flex"
            >
              Minha senha
            </Link>
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium hover:bg-muted"
              onClick={async () => {
                await signOut();
                setActiveCompanyId(null);
                queryClient.clear();
                await navigate({ to: "/login" });
              }}
            >
              <Icon name="logout" size={16} strokeWidth={2} />
              Sair
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
      <NoticeArea />
    </div>
  );
}
